/**
 * Settings → Bloqueados — list of every user the current account has
 * blocked, with an inline "Desbloquear" action per row.
 *
 * Uses the cascade-invalidation hooks: tapping Desbloquear removes the
 * row, then immediately the cached vehicles + profiles + conversations
 * for that user become re-fetchable, so the next time the user opens
 * Explore / chat they see the un-blocked content again.
 *
 * Unblocking is confirmed, not one-tap. Blocking already goes through a
 * titled modal with Cancel/Confirm (components/ui/BlockUserButton.tsx), and
 * the reverse direction is exactly as consequential: it lets someone message
 * you again. An asymmetric guard trains the user that only one of the two
 * directions is worth reading.
 */
import { useState } from 'react'
import { View, Text } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Pressable } from '@/components/ui/Pressable'
import { Avatar } from '@/components/ui/Avatar'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ScreenState } from '@/components/ui/ScreenState'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { UserMinus } from '@/components/ui/icons'
import { useBlockedUsers, useUnblockUser } from '@/lib/hooks/use-user-blocks'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { toast } from '@/lib/toast'
import { errorMessage } from '@/lib/utils/observability'
import type { UserBlockRow } from '@/lib/services/user-block.service'

/** Height of one blocked row — the skeleton stands in for exactly this. */
const BLOCK_ROW_HEIGHT = 72

export default function BlockedUsersScreen() {
  const { t } = useTranslation()
  const insets = useSafeAreaInsets()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  // `useBlockedUsers()` returns the raw useQuery result, so it plugs straight
  // into ScreenState — which is what guarantees a failed fetch renders an
  // error with a retry instead of "No tenés a nadie bloqueado", and a load
  // renders row-shaped skeletons instead of a bare spinner.
  const blocksQuery = useBlockedUsers()
  const unblock = useUnblockUser()

  const [pendingUnblock, setPendingUnblock] = useState<UserBlockRow | null>(null)

  const handleConfirmUnblock = async () => {
    if (!pendingUnblock) return
    try {
      await unblock.mutateAsync({ blockedId: pendingUnblock.blockedId })
      toast.success(t('block.unblock_success_toast'))
    } catch (err) {
      toast.error(errorMessage(err, t('block.error_generic')))
    } finally {
      // Close on failure too. Leaving the dialog mounted put the modal on top
      // of its own error toast with the spinner already gone — the same screen,
      // no visible change, nothing to react to.
      setPendingUnblock(null)
    }
  }

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader title={t('settings.blocked_users')} backTestID="blocked-users.back" />

      <ScrollView
        contentContainerStyle={{
          // `flexGrow` so the empty state can claim the rest of the screen
          // instead of stacking under the intro line with ~1200pt of nothing
          // beneath it.
          flexGrow: 1,
          paddingHorizontal: SPACING.lg,
          paddingBottom: insets.bottom + SPACING.xl,
        }}
        showsVerticalScrollIndicator={false}
      >
        <ScreenState
          query={blocksQuery}
          skeleton={
            <View
              style={{ gap: SPACING.sm, marginTop: SPACING.md }}
              testID="blocked-users.skeleton"
            >
              {[0, 1, 2].map((index) => (
                <Skeleton
                  key={index}
                  width="100%"
                  height={BLOCK_ROW_HEIGHT}
                  borderRadius={RADIUS.md}
                  staggerIndex={index}
                />
              ))}
            </View>
          }
        >
          {(blocks) =>
            // Empty is rendered here rather than through ScreenState's `empty`
            // config: that path forwards only a title, so `bloom` drew no icon
            // and the screen contradicted /profile/notifications one tap away.
            // The intro line doubles as the supporting sentence — with no list
            // to introduce it belongs under the title, not left-aligned above
            // a centred orphan.
            blocks.length === 0 ? (
              <View style={{ flex: 1, justifyContent: 'center' }} testID="blocked-users.empty">
                <EmptyState
                  variant="bloom"
                  illustration={
                    <UserMinus size={36} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />
                  }
                  title={t('block.empty_state')}
                  description={t('block.list_intro')}
                />
              </View>
            ) : (
              <View style={{ gap: SPACING.md, marginTop: SPACING.xs }}>
                <Text
                  style={{
                    fontSize: 14,
                    lineHeight: 20,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                  }}
                >
                  {t('block.list_intro')}
                </Text>

                <View style={{ gap: SPACING.sm }}>
                  {blocks.map((row) => (
                    <View
                      key={row.id}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        paddingHorizontal: SPACING.md,
                        paddingVertical: SPACING.md,
                        borderRadius: RADIUS.md,
                        borderWidth: 1,
                        borderColor: colors.border,
                        backgroundColor: colors.surfaceSubtle,
                        gap: SPACING.md,
                      }}
                    >
                      <Avatar
                        size="sm"
                        source={row.avatarUrl}
                        fallback={row.displayName ?? '?'}
                        colorSeed={row.blockedId}
                        accessibilityLabel={row.displayName ?? row.blockedId}
                      />
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          style={{
                            fontFamily: FONTS.bodyBold,
                            fontSize: 15,
                            color: colors.textPrimary,
                          }}
                          numberOfLines={1}
                        >
                          {row.displayName ?? `${row.blockedId.slice(0, 8)}…`}
                        </Text>
                        {row.reason ? (
                          <Text
                            style={{
                              marginTop: 2,
                              fontFamily: FONTS.body,
                              fontSize: 12,
                              color: colors.textMuted,
                            }}
                            numberOfLines={2}
                          >
                            {row.reason}
                          </Text>
                        ) : null}
                      </View>
                      <Pressable
                        onPress={() => setPendingUnblock(row)}
                        disabled={unblock.isPending}
                        style={({ pressed }) => ({
                          minHeight: 44,
                          justifyContent: 'center',
                          paddingHorizontal: SPACING.md,
                          paddingVertical: SPACING.sm,
                          borderRadius: RADIUS.md,
                          backgroundColor: colors.surface,
                          borderWidth: 1,
                          borderColor: colors.border,
                          opacity: pressed || unblock.isPending ? 0.7 : 1,
                        })}
                        accessibilityRole="button"
                        accessibilityLabel={t('block.unblock_action')}
                        testID={`blocked-users.unblock-${row.blockedId}`}
                      >
                        <Text
                          style={{
                            fontFamily: FONTS.body,
                            fontSize: 13,
                            color: colors.textBrand,
                          }}
                        >
                          {t('block.unblock_action')}
                        </Text>
                      </Pressable>
                    </View>
                  ))}
                </View>
              </View>
            )
          }
        </ScreenState>
      </ScrollView>

      <ConfirmDialog
        visible={pendingUnblock !== null}
        title={t('block.unblock_confirm_title', {
          name: pendingUnblock
            ? (pendingUnblock.displayName ?? `${pendingUnblock.blockedId.slice(0, 8)}…`)
            : '',
        })}
        message={t('block.unblock_confirm_body')}
        confirmLabel={t('block.unblock_action')}
        cancelLabel={t('common.cancel')}
        confirmLoading={unblock.isPending}
        destructive={false}
        onConfirm={() => void handleConfirmUnblock()}
        onCancel={() => {
          if (unblock.isPending) return
          setPendingUnblock(null)
        }}
        testIDPrefix="blocked-users.unblock-confirm"
      />
    </ScreenContainer>
  )
}
